import React, { useState } from 'react';
import { Briefcase, CreditCard, Loader, Shield, UserCog } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { BillingPage } from './BillingPage';
import { ProfessionalProfilePage } from './ProfessionalProfilePage';
import { FirmDetailsPage } from './FirmDetailsPage';
import { AuditPage } from './AuditPage';
import { useAuth } from '../contexts/AuthContext';
import { authApi, PersonaType } from '../services/authApi';

type AccountTab = 'billing' | 'credentials' | 'activity';

export const AccountPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get('tab') as AccountTab) || 'billing';
  const { user, refreshUser } = useAuth();
  const isSolicitor = user?.personaType === 'SOLICITOR';
  const [switching, setSwitching] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const tabs: { key: AccountTab; label: string; icon: React.ElementType }[] = [
    { key: 'billing', label: 'Profile & Billing', icon: CreditCard },
    { key: 'credentials', label: isSolicitor ? 'Firm Details' : 'Professional Profile', icon: Briefcase },
    { key: 'activity', label: 'Activity Log', icon: Shield },
  ];

  const handleTabChange = (tab: AccountTab) => {
    setSearchParams(tab === 'billing' ? {} : { tab });
  };

  const handleSwitchPersona = async () => {
    const next: PersonaType = isSolicitor ? 'EXPERT_WITNESS' : 'SOLICITOR';
    try {
      setSwitching(true);
      await authApi.updateProfile({ personaType: next });
      await refreshUser();
      setShowConfirm(false);
    } finally {
      setSwitching(false);
    }
  };

  return (
    <div>
      {/* Tab bar */}
      <div className="border-b border-medical-200 dark:border-medical-700 bg-white dark:bg-medical-800 px-6 pt-4">
        <nav className="-mb-px flex space-x-8">
          {tabs.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => handleTabChange(key)}
              className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === key
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-medical-500 dark:text-medical-400 hover:text-medical-700 dark:hover:text-medical-200 hover:border-medical-300'
              }`}
            >
              <Icon className="h-4 w-4" />
              <span>{label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Persona switcher — only on the credentials tab */}
      {activeTab === 'credentials' && (
        <div className="px-8 pt-6">
          <div className="max-w-3xl mx-auto bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <UserCog className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
              <div className="text-sm">
                <div className="font-medium text-blue-900 dark:text-blue-100">
                  Signed in as {isSolicitor ? 'Solicitor' : 'Expert Witness'}
                </div>
                <div className="text-xs text-blue-700 dark:text-blue-300">
                  Switching persona changes your profile fields, document labels, and template categories. Your existing data is preserved.
                </div>
              </div>
            </div>
            <button
              onClick={() => setShowConfirm(true)}
              className="text-sm font-medium text-blue-700 dark:text-blue-200 hover:text-blue-900 dark:hover:text-blue-100 underline whitespace-nowrap"
            >
              Switch to {isSolicitor ? 'Expert Witness' : 'Solicitor'}
            </button>
          </div>
        </div>
      )}

      {/* Tab content */}
      {activeTab === 'billing' && <BillingPage />}
      {activeTab === 'credentials' && (isSolicitor ? <FirmDetailsPage /> : <ProfessionalProfilePage />)}
      {activeTab === 'activity' && <AuditPage />}

      {/* Switch confirmation modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-medical-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-medical-900 dark:text-medical-100 mb-2">
              Switch to {isSolicitor ? 'Expert Witness' : 'Solicitor'}?
            </h3>
            <p className="text-sm text-medical-600 dark:text-medical-400 mb-6">
              Your profile fields, document labels, and template categories will change to suit the new persona.
              Your existing data is preserved — switch back at any time.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowConfirm(false)}
                disabled={switching}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleSwitchPersona}
                disabled={switching}
                className="btn-primary flex items-center gap-2"
              >
                {switching && <Loader className="h-4 w-4 animate-spin" />}
                <span>Switch persona</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
